import { useCallback, useEffect, useState } from "react";
import Button from "../Common/Button";
import ConfirmModal from "../Common/ConfirmModal";
import Input from "../Common/Input";
import Label from "../Common/Label";
import LoadingSpinner from "../Common/LoadingSpinner";
import Pagination from "../Common/Pagination";
import {
  addFMSCustomerAllowedSerialNumbers,
  getFMSCustomerAllowedSerialNumbers,
  removeFMSCustomerAllowedSerialNumber,
} from "../../lib/api";
import { trackEvent } from "../../lib/analytics";
import { AnalyticsSchemas, SearchContext } from "../../lib/analyticsEvents";

const PAGE_SIZE = 20;

const parseSerialNumbers = (value = "") => [
  ...new Set(
    value
      .split(/[\s,]+/)
      .map((serialNumber) => serialNumber.trim())
      .filter(Boolean)
  ),
];

const AllowedSerialNumbersPanel = ({ customerUnid, onNotify }) => {
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addInput, setAddInput] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [serialNumberToRemove, setSerialNumberToRemove] = useState(null);

  const pageCount = Math.ceil(totalCount / PAGE_SIZE);
  const parsedSerialNumbers = parseSerialNumbers(addInput);

  const loadSerialNumbers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getFMSCustomerAllowedSerialNumbers(customerUnid, {
        pageNumber: currentPage + 1,
        pageSize: PAGE_SIZE,
        serialNumberFragment: searchText,
      });
      const payload = response?.data?.data || {};
      setSerialNumbers(payload.serialNumbers || []);
      setTotalCount(payload.count || 0);
    } catch (error) {
      setSerialNumbers([]);
      setTotalCount(0);
      onNotify(error.message || "Failed to fetch allowed serial numbers.", "failed");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, customerUnid, onNotify, searchText]);

  useEffect(() => {
    loadSerialNumbers();
  }, [loadSerialNumbers]);

  const applySearch = () => {
    const trimmedInput = searchInput.trim();
    setSearchText(trimmedInput);
    setCurrentPage(0);
    if (trimmedInput) {
      trackEvent(AnalyticsSchemas.webSearch, {
        search_context: SearchContext.allowedSerialNumbers,
        term_length: trimmedInput.length,
      });
    }
  };

  const closeAddModal = () => {
    if (isAdding) return;
    setIsAddOpen(false);
    setAddInput("");
  };

  const handleAddSerialNumbers = async () => {
    if (parsedSerialNumbers.length === 0) return;

    setIsAdding(true);
    try {
      await addFMSCustomerAllowedSerialNumbers(customerUnid, parsedSerialNumbers);
      setIsAddOpen(false);
      setAddInput("");
      onNotify(
        `${parsedSerialNumbers.length} serial number${parsedSerialNumbers.length === 1 ? "" : "s"} added.`,
        "success"
      );

      const isAlreadyShowingEverything = currentPage === 0 && !searchText;
      setCurrentPage(0);
      setSearchInput("");
      setSearchText("");

      if (isAlreadyShowingEverything) {
        loadSerialNumbers();
      }
    } catch (error) {
      onNotify(error.message || "Failed to add serial numbers.", "failed");
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveConfirmed = async () => {
    if (!serialNumberToRemove) return;

    setIsRemoving(true);
    try {
      await removeFMSCustomerAllowedSerialNumber(customerUnid, serialNumberToRemove);
      setSerialNumberToRemove(null);
      onNotify("Serial number removed.", "success");

      const nextPageCount = Math.ceil(Math.max(totalCount - 1, 0) / PAGE_SIZE);
      if (currentPage > 0 && currentPage >= nextPageCount) {
        setCurrentPage(Math.max(nextPageCount - 1, 0));
      } else {
        loadSerialNumbers();
      }
    } catch (error) {
      onNotify(error.message || "Failed to remove serial number.", "failed");
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 shadow-appShadow overflow-hidden"
      data-testid="allowed-serial-numbers-panel"
    >
      <div className="border-b border-[#E6E8EC] dark:border-white/10 px-5 py-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-base font-semibold text-dark-grey dark:text-white">
            Allowed Serial Numbers
          </h3>
          <p className="text-sm text-dark-grey/70 dark:text-white/60 mt-1">
            {totalCount} total
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 lg:items-center">
          <div className="flex items-center gap-2 rounded-md border border-[#E6E8EC] dark:border-white/10 px-3 py-2 w-full sm:w-64">
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  applySearch();
                }
              }}
              placeholder="Filter serial numbers..."
              aria-label="Filter serial numbers"
              className="bg-transparent w-full text-sm text-dark-grey dark:text-white outline-none placeholder:text-dark-grey/60 dark:placeholder:text-white/50"
              data-testid="serial-number-search-input"
            />
            <Button
              className="shrink-0"
              onClick={applySearch}
              aria-label="Search serial numbers"
              data-testid="serial-number-search-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
                <circle cx="9.58342" cy="9.58366" r="7.91667" strokeWidth="1.5" className="stroke-dark-grey dark:stroke-white" />
                <path
                  d="M15.4167 15.417L18.3334 18.3337"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  className="stroke-dark-grey dark:stroke-white"
                />
              </svg>
            </Button>
          </div>
          <Button
            className="flex items-center justify-center gap-2 bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition w-full sm:w-auto"
            onClick={() => setIsAddOpen(true)}
            data-testid="add-serial-numbers-button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 5V19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M5 12H19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            Add Serial Numbers
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-48">
          <LoadingSpinner />
        </div>
      ) : serialNumbers.length === 0 ? (
        <div className="px-5 py-12 text-center" data-testid="allowed-serial-numbers-empty">
          <p className="text-base font-semibold text-dark-grey dark:text-white">
            No serial numbers found
          </p>
          <p className="mt-2 text-sm text-dark-grey/70 dark:text-white/60">
            {searchText
              ? "No allowed serial numbers match that filter."
              : "Add serial numbers to let this customer claim those units."}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[#E6E8EC] dark:divide-white/10">
          {serialNumbers.map((serialNumber) => (
            <div
              key={serialNumber}
              className="px-5 py-3 flex items-center justify-between gap-4"
              data-testid={`allowed-serial-number-row-${serialNumber}`}
            >
              <span className="text-sm font-medium text-dark-grey dark:text-white break-all">
                {serialNumber}
              </span>
              <Button
                className="px-3 py-1.5 rounded-md border border-[#EF4444] text-[#EF4444] text-xs font-semibold hover:bg-[#EF4444] hover:text-white transition shrink-0"
                onClick={() => setSerialNumberToRemove(serialNumber)}
                data-testid={`remove-serial-number-${serialNumber}`}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      {pageCount > 1 && (
        <div className="px-5 pb-5">
          <Pagination
            currentPage={currentPage}
            pageCount={pageCount}
            onPageChange={({ selected }) => setCurrentPage(selected)}
          />
        </div>
      )}

      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div
            className="w-full max-w-md rounded-2xl bg-white dark:bg-darkThemeModalBg shadow-appShadow"
            data-testid="add-serial-numbers-modal"
          >
            <div className="flex items-center justify-between border-b border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <h3 className="text-lg font-semibold text-dark-grey dark:text-white">
                Add Serial Numbers
              </h3>
              <Button
                className="text-dark-grey/70 dark:text-white/70 hover:text-dark-grey dark:hover:text-white"
                onClick={closeAddModal}
                aria-label="Close"
                data-testid="add-serial-numbers-close"
              >
                ✕
              </Button>
            </div>
            <div className="px-6 py-4 space-y-2">
              <Label className="text-sm text-dark-grey dark:text-white" htmlFor="serial-numbers-input">
                Serial Numbers
              </Label>
              <textarea
                id="serial-numbers-input"
                value={addInput}
                onChange={(event) => setAddInput(event.target.value)}
                placeholder="1101-T1-0000001, 1101-T1-0000002"
                className="min-h-[140px] w-full rounded-md border border-[#E6E8EC] dark:border-[#0B2E57] bg-white dark:bg-[#0F3B7C] px-3 py-2 text-sm text-dark-grey dark:text-white placeholder:text-dark-grey/60 dark:placeholder:text-white/60 outline-none focus:border-theme resize-y"
                data-testid="serial-numbers-textarea"
              />
              <p className="text-xs text-dark-grey/70 dark:text-white/60">
                Separate serial numbers with commas, spaces, or new lines.
                {parsedSerialNumbers.length > 0 &&
                  ` ${parsedSerialNumbers.length} will be added.`}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <Button
                className="bg-white text-dark-grey border border-[#E6E8EC] dark:border-theme dark:text-white dark:bg-transparent px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme hover:text-white transition-all duration-300 ease-in-out"
                onClick={closeAddModal}
                data-testid="add-serial-numbers-cancel"
              >
                Cancel
              </Button>
              <Button
                className="bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleAddSerialNumbers}
                disabled={isAdding || parsedSerialNumbers.length === 0}
                data-testid="add-serial-numbers-submit"
              >
                {isAdding ? "Adding..." : "Add"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {serialNumberToRemove && (
        <ConfirmModal
          title="Remove Serial Number"
          subText="The customer will no longer be able to claim this unit."
          confirmMessage={`${serialNumberToRemove} will be removed from the allowed list.`}
          cancelText="Cancel"
          submitText={isRemoving ? "Removing..." : "Remove"}
          disableCancel={isRemoving}
          disableSubmit={isRemoving}
          onCancel={() => {
            if (isRemoving) return;
            setSerialNumberToRemove(null);
          }}
          onSubmit={handleRemoveConfirmed}
        />
      )}
    </div>
  );
};

export default AllowedSerialNumbersPanel;
